import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowUpRight, Activity, Apple, Brain, Trophy, Play, Check } from "lucide-react";
import { Reveal } from "../components/Reveal";
import { useContent } from "../lib/api";
import { COACH_GYM, VIDEO_HERO, VIDEO_POWER } from "../lib/brand";

const STATS = [
  { v: "500+", l: "Clients Coached" },
  { v: "8 yrs", l: "Experience" },
  { v: "4.9★", l: "Avg Rating" },
  { v: "50+", l: "Programs" },
];

const PILLARS = [
  { icon: Activity, t: "Smart Training", d: "Personalised workouts that adapt to your goal, level and equipment." },
  { icon: Apple, t: "Real Nutrition", d: "Sustainable, science-backed meal plans — no crash diets or gimmicks." },
  { icon: Brain, t: "AI Assistant 24/7", d: "Instant, evidence-based answers, exercise swaps and accountability in your pocket.", action: "assistant" },
  { icon: Trophy, t: "Track & Win", d: "Streaks, challenges and badges that keep you coming back daily." },
];

export default function Home() {
  const { data: plans } = useContent("plans");
  const { data: blogs } = useContent("blogs");
  const { data: testimonials } = useContent("testimonials");
  const myths = blogs.filter((b) => b.tag === "Myth Buster").slice(0, 3);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative min-h-[100svh] flex items-end overflow-hidden">
        <div className="absolute inset-0 z-0">
          <video
            src={VIDEO_HERO}
            poster={COACH_GYM}
            autoPlay
            muted
            loop
            playsInline
            preload="metadata"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
          <div className="absolute inset-0 bg-black/35" />
        </div>
        <div className="relative z-10 mx-auto max-w-[1400px] w-full px-5 lg:px-8 pb-20 md:pb-28">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="text-xs uppercase tracking-[0.3em] text-primary font-bold mb-5">
            Personal Coaching &middot; @beingfitforyou
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2, duration: 0.7 }}
            className="font-display font-900 uppercase text-white text-6xl sm:text-7xl md:text-8xl lg:text-[8.5vw] leading-[0.85] tracking-tighter">
            Fitness for<br />every body.
          </motion.h1>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}
            className="text-white/85 text-lg mt-6 max-w-xl">
            Science-backed training, honest nutrition and a coach in your pocket — built around your goal, your body, your life.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.6 }}
            className="mt-9 flex flex-wrap gap-4">
            <Link to="/assessment" data-testid="hero-assessment"
              className="group flex items-center gap-2 bg-primary text-primary-foreground px-8 py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.03] transition-transform">
              Take Free Assessment <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/programs" data-testid="hero-programs"
              className="bg-white/10 backdrop-blur border border-white/40 text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:bg-white hover:text-black transition-colors">
              View Programs
            </Link>
          </motion.div>
        </div>
      </section>

      {/* STATS */}
      <section className="border-b border-border">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8 py-12 grid grid-cols-2 md:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.06}>
              <div className="font-display font-800 text-5xl md:text-6xl leading-none">{s.v}</div>
              <div className="text-xs uppercase tracking-[0.15em] text-muted-foreground mt-1">{s.l}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PILLARS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-8 py-20 md:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-4">Why BeingFitForYou</p>
          <h2 className="font-display font-800 uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight max-w-3xl">
            Everything you need to actually stay consistent.
          </h2>
        </Reveal>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4 mt-14">
          {PILLARS.map((p, i) => (
            <Reveal key={p.t} delay={i * 0.07}>
              <div
                data-testid={p.action ? "pillar-assistant" : undefined}
                onClick={p.action === "assistant" ? () => window.dispatchEvent(new Event("open-assistant")) : undefined}
                className={`border border-border rounded-lg p-7 h-full bg-card hover:border-primary transition-colors ${p.action ? "cursor-pointer" : ""}`}>
                <div className="grid place-items-center w-12 h-12 rounded-full bg-primary/10 text-primary mb-5"><p.icon size={22} /></div>
                <h3 className="font-display font-700 uppercase text-2xl tracking-tight">{p.t}</h3>
                <p className="text-muted-foreground text-sm mt-2">{p.d}</p>
                {p.action === "assistant" && <span className="inline-block mt-3 text-xs uppercase tracking-[0.12em] font-bold text-primary">Chat now →</span>}
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* PROGRAMS */}
      <section className="bg-card border-y border-border">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8 py-20 md:py-28">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-4">Coaching Programs</p>
              <h2 className="font-display font-800 uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight">Choose your path</h2>
            </Reveal>
            <Link to="/programs" className="text-xs uppercase tracking-[0.15em] font-bold flex items-center gap-2 hover:text-primary transition-colors">All Programs <ArrowUpRight size={16} /></Link>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.08}>
                <Link to="/programs" data-testid="home-plan-card" className="group block border border-border rounded-lg overflow-hidden bg-background h-full hover:border-primary transition-colors">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.12em] text-primary font-bold mb-2"><span>{p.level}</span><span className="text-muted-foreground">/ {p.duration}</span></div>
                    <h3 className="font-display font-700 uppercase text-3xl tracking-tight">{p.title}</h3>
                    <div className="mt-4 font-display font-800 text-3xl text-primary">{p.price}</div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* MYTH BUSTERS */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-8 py-20 md:py-28">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-4">Fitness Myth Busters</p>
          <h2 className="font-display font-800 uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-12">Stop believing the noise.</h2>
        </Reveal>
        <div className="grid gap-6 md:grid-cols-3">
          {myths.map((b, i) => (
            <Reveal key={b.id} delay={i * 0.08}>
              <Link to={`/blog/${b.id}`} data-testid="home-myth-card" className="group flex flex-col justify-between border border-border rounded-lg p-7 h-full bg-card hover:border-primary transition-colors">
                <div>
                  <span className="text-[10px] uppercase tracking-[0.15em] text-primary font-bold">Myth vs Fact</span>
                  <h3 className="font-display font-700 uppercase text-2xl tracking-tight mt-3 leading-tight">{b.title}</h3>
                  <p className="text-muted-foreground text-sm mt-3 line-clamp-3">{b.excerpt}</p>
                </div>
                <span className="inline-flex items-center gap-2 mt-6 text-xs uppercase tracking-[0.15em] font-bold group-hover:text-primary transition-colors">Read the truth <ArrowUpRight size={14} /></span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-card border-y border-border">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-8 py-20 md:py-28">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.25em] text-primary font-bold mb-4">Real Results</p>
            <h2 className="font-display font-800 uppercase text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight mb-12">People, transformed.</h2>
          </Reveal>
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {testimonials.map((t, i) => (
              <Reveal key={t.id} delay={i * 0.06}>
                <div className="border border-border rounded-lg p-6 bg-background h-full flex flex-col">
                  <div className="text-primary text-sm mb-3">{"★".repeat(t.rating || 5)}</div>
                  <p className="text-sm leading-relaxed flex-1">&ldquo;{t.quote}&rdquo;</p>
                  <div className="mt-5 pt-4 border-t border-border flex items-center gap-3">
                    <img src={t.image} alt={t.name} className="w-10 h-10 rounded-full object-cover" />
                    <div>
                      <div className="font-bold text-sm">{t.name}</div>
                      <div className="text-[11px] uppercase tracking-wide text-primary font-bold">{t.result}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-[1400px] px-5 lg:px-8 py-24">
        <Reveal>
          <div className="relative overflow-hidden rounded-2xl border border-border p-10 md:p-16 text-center min-h-[320px] flex flex-col justify-center">
            <video src={VIDEO_POWER} autoPlay muted loop playsInline preload="metadata" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-primary/85 mix-blend-multiply" />
            <div className="absolute inset-0 bg-black/25" />
            <div className="relative text-white">
              <h2 className="font-display font-900 uppercase text-4xl sm:text-5xl md:text-6xl leading-none tracking-tight">Ready to start?</h2>
              <p className="mt-4 max-w-xl mx-auto text-white/90">Take the 2-minute assessment and get a personalised recommendation + a free 7-day challenge.</p>
              <Link to="/assessment" data-testid="cta-assessment" className="inline-flex items-center gap-2 mt-8 bg-black text-white px-8 py-4 rounded-full text-xs uppercase tracking-[0.15em] font-bold hover:scale-[1.03] transition-transform">
                <Play size={14} /> Take Free Assessment
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
